import Button from "@mui/material/Button";
import jsPDF from "jspdf";
import React from "react";
import { formatDateUtil } from "../helpers/date";
import { Spielort } from "../interfaces";
import { displayTeamName } from "./TeamCard";
import { useTeamScheduleContext } from "./TeamScheduleContext";

const DownloadSchedulePDFButton: React.FC = () => {
  const { view, filteredMatches, team } = useTeamScheduleContext();

  const getTitle = () => {
    const teamName = team?.name || "Team";
    if (view === "home") return `Heimspielplan – ${teamName}`;
    if (view === "away") return `Auswärtsspielplan – ${teamName}`;
    return `Spielplan – ${teamName}`;
  };

  const handleDownloadPDF = () => {
    if (!team) return;
    const doc = new jsPDF();
    doc.setFontSize(16);
    doc.text(getTitle(), 10, 15);
    doc.setFontSize(12);
    const headers = ["Heim", "Gast", "Datum", "Spielort"];
    const colWidths = [50, 50, 30, 60];
    let y = 30;
    let x = 10;
    doc.setFillColor(242, 233, 228);
    doc.rect(
      x,
      y,
      colWidths.reduce((a, b) => a + b, 0),
      10,
      "F"
    );
    let colX = x;
    headers.forEach((header, i) => {
      doc.text(header, colX + 2, y + 7);
      doc.line(colX, y, colX, y + 10);
      colX += colWidths[i];
    });
    doc.line(colX, y, colX, y + 10);
    doc.line(x, y, colX, y);
    doc.line(x, y + 10, colX, y + 10);
    y += 10;
    filteredMatches.forEach((m) => {
      let rowX = x;
      // Handle team_away as string or object
      let awayName = "-";
      if (
        typeof m.team_away === "object" &&
        m.team_away !== null &&
        "name" in m.team_away
      ) {
        awayName = (m.team_away as { name: string }).name;
      } else if (typeof m.team_away === "string") {
        awayName = m.team_away;
      }
      // Handle spielort as object or string
      let spielortLines: string[] = ["-"];
      if (m.spielort) {
        if (typeof m.spielort === "object" && m.spielort !== null) {
          // Defensive: check for nested fields and flatten if needed
          const s = m.spielort as Spielort;
          // Some APIs may nest spielort as { spielort: { name, street, city } }
          spielortLines = [s.name, s.street, s.plzOrt];
        }
      }
      const row = [
        m.team_home.name,
        displayTeamName(String(awayName)),
        formatDateUtil(m.date),
        spielortLines[0],
      ];
      // Calculate max number of lines for this row (for row height)
      const extraSpielortLines = spielortLines.slice(1).filter(Boolean);
      const rowHeight = 10 + extraSpielortLines.length * 4;
      // Draw main row
      row.forEach((cell, i) => {
        doc.text(String(cell), rowX + 2, y + 7);
        doc.line(rowX, y, rowX, y + rowHeight);
        rowX += colWidths[i];
      });
      // Add street and city in smaller font below the name, in the Spielort column only
      if (extraSpielortLines.length > 0) {
        doc.setFontSize(9);
        let offset = 11; // Tighter spacing
        if (extraSpielortLines[0]) {
          // Street (with comma if city also present)
          let streetLine = extraSpielortLines[0];
          if (extraSpielortLines[1]) {
            streetLine += ",";
          }
          doc.text(
            String(streetLine),
            x + colWidths[0] + colWidths[1] + colWidths[2] + 2,
            y + offset
          );
          offset += 4;
        }
        if (extraSpielortLines[1]) {
          // City (plzOrt)
          doc.text(
            String(extraSpielortLines[1]),
            x + colWidths[0] + colWidths[1] + colWidths[2] + 2,
            y + offset
          );
        }
        doc.setFontSize(12);
      }
      // Draw row bottom and right border
      doc.line(rowX, y, rowX, y + rowHeight);
      doc.line(x, y, rowX, y);
      doc.line(x, y + rowHeight, rowX, y + rowHeight);
      y += rowHeight;
      if (y > 270) {
        doc.addPage();
        y = 20;
      }
    });
    doc.save(getTitle().replace(/\s+/g, "_") + ".pdf");
  };

  return (
    <Button
      variant="contained"
      onClick={handleDownloadPDF}
      sx={{
        mt: 2,
        mb: 2,
        textTransform: "none",
        fontWeight: 600,
      }}
    >
      Spielplan als PDF herunterladen
    </Button>
  );
};

export default DownloadSchedulePDFButton;
